import { logout, useAppDispatch, useAppSelector } from "@vv/state";
import { ROUTES } from "@vv/types";
import { Header, Sidebar, useThemeMode, type SidebarItem } from "@vv/ui";
import { NotificationBell } from "@vv/notifications-frontend";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import AssignmentIcon from "@mui/icons-material/AssignmentOutlined";
import BarChartIcon from "@mui/icons-material/BarChartOutlined";
import DashboardIcon from "@mui/icons-material/DashboardOutlined";
import GroupsIcon from "@mui/icons-material/GroupsOutlined";
import HistoryIcon from "@mui/icons-material/HistoryOutlined";
import PeopleAltIcon from "@mui/icons-material/PeopleAltOutlined";
import ViewKanbanIcon from "@mui/icons-material/ViewKanbanOutlined";
import { useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { getRuntimeConfig } from "../config/runtime-config";

const config = getRuntimeConfig();

/** Authenticated shell: Header + Sidebar + <Outlet /> for the matched child route. */
export function AppLayout() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { mode, toggleMode } = useThemeMode();
  const user = useAppSelector((state) => state.auth.user);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const sidebarItems: SidebarItem[] = [
    {
      key: "dashboard",
      label: "Dashboard",
      icon: <DashboardIcon fontSize="small" />,
      selected: location.pathname === ROUTES.home,
      onSelect: () => navigate(ROUTES.home),
    },
    {
      key: "requests",
      label: "Requests",
      icon: <AssignmentIcon fontSize="small" />,
      selected: location.pathname.startsWith(ROUTES.requests),
      onSelect: () => navigate(ROUTES.requests),
    },
    {
      key: "kanban",
      label: "Kanban",
      icon: <ViewKanbanIcon fontSize="small" />,
      selected: location.pathname.startsWith(ROUTES.kanban),
      onSelect: () => navigate(ROUTES.kanban),
    },
    {
      key: "teams",
      label: "Teams",
      icon: <GroupsIcon fontSize="small" />,
      selected: location.pathname.startsWith(ROUTES.teams),
      onSelect: () => navigate(ROUTES.teams),
    },
    {
      key: "users",
      label: "Users",
      icon: <PeopleAltIcon fontSize="small" />,
      selected: location.pathname.startsWith(ROUTES.users),
      onSelect: () => navigate(ROUTES.users),
    },
    {
      key: "analytics",
      label: "Analytics",
      icon: <BarChartIcon fontSize="small" />,
      selected: location.pathname.startsWith(ROUTES.analytics),
      onSelect: () => navigate(ROUTES.analytics),
    },
    ...(user?.isOwner || user?.isViewer
      ? [
          {
            key: "audit-log",
            label: "Audit Log",
            icon: <HistoryIcon fontSize="small" />,
            selected: location.pathname.startsWith(ROUTES.auditLog),
            onSelect: () => navigate(ROUTES.auditLog),
          },
        ]
      : []),
  ];

  async function handleLogout() {
    await dispatch(logout());
    navigate(ROUTES.login, { replace: true });
  }

  return (
    <Box sx={{ display: "flex" }}>
      <Header
        appName={config.appName}
        userLabel={user?.displayName ?? user?.email}
        themeMode={mode}
        onToggleTheme={toggleMode}
        onToggleSidebar={() => setSidebarOpen((prev) => !prev)}
        onLogout={handleLogout}
        extraActions={<NotificationBell apiBaseUrl={config.apiBaseUrl} />}
      />
      {sidebarOpen ? (
        <Sidebar items={sidebarItems} variant="permanent" onClose={() => setSidebarOpen(false)} />
      ) : null}
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
}
